import React, { useState } from "react";
import { Button } from "antd";
import { UserOutlined, PlusOutlined, FilterOutlined } from "@ant-design/icons";
import UserFilters from "./UserFilters";
// import { useRoles } from "../../hooks/useRoles"; // Import roles hook
import { useUserTypes } from "../../hooks/useUsers"; // Import user types hook
import { useLocations } from "../../hooks/useLocation"; // Import locations hook
import { useAuth } from "../../hooks/useAuth";

interface UserHeaderProps {
  onAddUser: () => void;
  onApplyFilters: (filters: Record<string, any>) => void;
  onClearFilters: () => void;
  canAddUsers: boolean; // New prop for permission check
  canSeeAllLocations: boolean; // New prop for location permission
}

const UserHeader: React.FC<UserHeaderProps> = ({
  onAddUser,
  onApplyFilters,
  onClearFilters,
  canAddUsers,
  canSeeAllLocations,
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const { data: user } = useAuth();

  // Get location ID from logged in user
  const locationId = user?.location?.id;

  // const { data: roles } = useRoles();
  const { data: userTypes } = useUserTypes();
  const { data: locations } = useLocations();

  // Default filters - staff users only, no guests
  const defaultFilters: Record<string, any> = {
    user_type_id: 2, // Staff user type ID
    include_guests: false,
  };

  // Only add location_id if user cannot see all locations AND has a locationId
  if (!canSeeAllLocations && locationId) {
    defaultFilters.location_id = locationId;
  }

  // Convert data to options format for select inputs
  // const roleOptions =
  //   roles?.map((role) => ({
  //     value: role.id,
  //     label: role.name,
  //   })) || [];

  // Fix for user_type_id filter - ensure it's converted properly
  // IMPORTANT: Include ALL user types including guests (don't filter them out)
  const userTypeOptions = userTypes
    ? Object.entries(userTypes).map(([id, name]) => {
        const option = {
          value: Number(id), // Ensure this is a number, not string
          label: name,
        };
        return option;
      })
    : [];

  const locationOptions =
    locations?.map((location) => ({
      value: location.id,
      label: location.name,
    })) || [];

  const allFilterFields = [
    { key: "name", label: "Name", type: "text" as const },
    { key: "email", label: "Email", type: "text" as const },
    { key: "phone", label: "Phone", type: "text" as const },
    { key: "whatsapp_phone", label: "WhatsApp", type: "text" as const },
    // { key: "date_of_birth", label: "Date of Birth", type: "date" as const },
    {
      key: "gender",
      label: "Gender",
      type: "select" as const,
      options: [
        { value: "male", label: "Male" },
        { value: "female", label: "Female" },
      ],
    },
    {
      key: "user_type_id",
      label: "User Type",
      type: "select" as const,
      options: userTypeOptions,
    },
    {
      key: "location_id",
      label: "Location",
      type: "select" as const,
      options: locationOptions,
      requiresPermission: true, // Only show if user has permission
    },
    // {
    //   key: "role_id",
    //   label: "Role",
    //   type: "select" as const,
    //   options: roleOptions,
    // },
    { key: "state", label: "State", type: "text" as const },
    { key: "status", label: "Status", type: "status" as const },
  ];

  // Filter out location field if user doesn't have permission
  const filterFields = allFilterFields.filter(
    (field) => !field.requiresPermission || canSeeAllLocations
  );

  return (
    <div className="mb-6">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-4 space-y-4 lg:space-y-0">
        <div className="flex items-center space-x-2">
          <UserOutlined className="text-xl text-primary" />
          <h1 className="text-lg lg:text-xl font-bold">User Management</h1>
        </div>

        <div className="flex space-x-3">
          {/* Only show Add User button if user has permission */}
          {canAddUsers && (
            <Button
              type="default"
              onClick={onAddUser}
              icon={<PlusOutlined />}
              className="border border-primary text-primary"
            >
              Add New User
            </Button>
          )}
          <Button
            icon={<FilterOutlined />}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filters
          </Button>
        </div>
      </div>

      {/* Enhanced Filters Section with All Required Filters */}
      {showFilters && (
        <UserFilters
          fields={filterFields}
          onApplyFilters={onApplyFilters}
          onClearFilters={onClearFilters}
          defaultFilters={defaultFilters}
        />
      )}
    </div>
  );
};

export default UserHeader;
